
// En prod: /api/auth (mismo origen). En dev: URL completa al backend (Docker en 8080 o PHP).
const API_BASE = process.env.REACT_APP_AUTH_API
  || (process.env.NODE_ENV === 'production' ? '/api/auth' : 'http://localhost:8080/api/auth');

export interface User {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    full_name: string;
    phone?: string;
    country?: string;
    city?: string;
    gender?: 'male' | 'female' | 'other';
    birth_date?: string;
    profile_image_url?: string;
    is_admin: boolean;
    /**
     * Bitmask de acceso (TINYINT en DB):
     *   0 = sin acceso
     *   1 = cursos
     *   2 = bootcamp
     *   4 = mentorias
     * Combinaciones: 3 = cursos+bootcamp, 5 = cursos+mentorias, 6 = bootcamp+mentorias, 7 = todo
     */
    has_access?: number;
    /** Si tiene contraseña propia (false = cuenta creada con Google sin password). */
    has_password?: boolean;
}

// --- Access level bitmask helpers ---

export const ACCESS_FLAGS = {
    NONE:      0,
    CURSOS:    1,
    BOOTCAMP:  2,
    MENTORIAS: 4,
} as const;

export const ACCESS_LABELS: Record<number, string> = {
    0: 'Sin acceso',
    1: 'Cursos',
    2: 'Bootcamp',
    3: 'Cursos + Bootcamp',
    4: 'Mentorías',
    5: 'Cursos + Mentorías',
    6: 'Bootcamp + Mentorías',
    7: 'Acceso completo',
};

export const hasAccessFlag = (user: User | null, flag: number): boolean =>
    !!user && ((user.has_access || 0) & flag) !== 0;

export const hasCursos    = (user: User | null) => hasAccessFlag(user, ACCESS_FLAGS.CURSOS);
export const hasBootcamp  = (user: User | null) => hasAccessFlag(user, ACCESS_FLAGS.BOOTCAMP);
export const hasMentorias = (user: User | null) => hasAccessFlag(user, ACCESS_FLAGS.MENTORIAS);
export const hasAnyAccess = (user: User | null) => !!user && ((user.has_access || 0) > 0 || user.is_admin);

export interface AuthResponse {
    success: boolean;
    message: string;
    user: User;
    token: string;
}

export interface VerifyResponse {
    success: boolean;
    message: string;
    user: User;
}

// Guardar token en localStorage
export const setToken = (token: string) => {
    localStorage.setItem('auth_token', token);
};

// Obtener token de localStorage
export const getToken = (): string | null => {
    return localStorage.getItem('auth_token');
};

// Eliminar token
export const removeToken = () => {
    localStorage.removeItem('auth_token');
};

// Login con email y password
export const login = async (email: string, password: string): Promise<AuthResponse> => {
    const response = await fetch(`${API_BASE}/login.php`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || 'Error al iniciar sesión');
    }

    // Guardar token
    if (data.token) {
        setToken(data.token);
    }

    return data;
};

// Google Login
export const googleLogin = async (credential: string): Promise<AuthResponse> => {
    const response = await fetch(`${API_BASE}/google.php`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credential }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || 'Error en autenticación con Google');
    }

    if (data.token) {
        setToken(data.token);
    }

    return data;
};

// Registro de nuevo usuario
export const register = async (
    email: string,
    password: string,
    firstName: string,
    lastName: string
): Promise<AuthResponse> => {
    const response = await fetch(`${API_BASE}/register.php`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            email,
            password,
            first_name: firstName,
            last_name: lastName
        }),
    });

    const data = await response.json();

    if (!response.ok) {
        throw new Error(data.error || 'Error al registrarse');
    }

    // Guardar token
    if (data.token) {
        setToken(data.token);
    }

    return data;
};

// Olvidé mi contraseña: genera una nueva y la envía al email
export const forgotPassword = async (email: string): Promise<{ success: boolean; message: string }> => {
    let response: Response;
    try {
        response = await fetch(`${API_BASE}/forgot-password.php`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email }),
        });
    } catch (e) {
        // Red/CORS: backend no responde (ej. Docker no corriendo en 8080)
        throw new Error(
            'No se pudo conectar con el servidor. En desarrollo, asegurate de que Docker (backend en puerto 8080) esté corriendo.'
        );
    }

    const text = await response.text();
    let data: { success?: boolean; message?: string; error?: string } = {};
    try {
        data = text ? JSON.parse(text) : {};
    } catch {
        throw new Error('El servidor respondió con un error. Intentá más tarde.');
    }

    if (!response.ok) {
        throw new Error(data.error || 'Error al solicitar nueva contraseña');
    }

    return { success: data.success ?? true, message: data.message ?? '' };
};

// Verificar token y obtener usuario actual
export const verifyToken = async (): Promise<User | null> => {
    const token = getToken();
    if (!token) return null;

    try {
        const response = await fetch(`${API_BASE}/verify.php`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            removeToken();
            return null;
        }

        const data: VerifyResponse = await response.json();
        return data.user;
    } catch (error) {
        removeToken();
        return null;
    }
};

// Logout
export const logout = () => {
    removeToken();
    window.location.href = '/';
};
